/**
 * Offline-aware API client with retry logic and caching
 * Wraps the existing api.js with offline capabilities
 */

import api from './api';
import {
  saveJobsToCache,
  getJobsFromCache,
  saveCategoriesToCache,
  getCategoriesFromCache,
  savePendingAction,
  getPendingActions,
  removePendingAction
} from './offlineStorage';

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000  // 10 seconds
};

/**
 * Calculate exponential backoff delay
 */
function getRetryDelay(attempt) {
  const delay = Math.min(
    RETRY_CONFIG.baseDelay * Math.pow(2, attempt),
    RETRY_CONFIG.maxDelay
  );
  // Add jitter to prevent thundering herd
  return delay + Math.random() * 1000;
}

/**
 * Sleep for a given duration
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch with retry logic
 */
async function fetchWithRetry(requestFn, options = {}) {
  const { maxRetries = RETRY_CONFIG.maxRetries } = options;
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await requestFn();
      return response;
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx) except 408 (timeout) and 429 (rate limit)
      if (error.response) {
        const status = error.response.status;
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
          throw error;
        }
      }

      // Don't retry if we're offline
      if (!navigator.onLine) {
        throw error;
      }

      // Wait before retrying
      if (attempt < maxRetries) {
        const delay = getRetryDelay(attempt);
        console.log(`[OfflineAPI] Retry ${attempt + 1}/${maxRetries} after ${delay}ms`);
        await sleep(delay);
      }
    }
  }

  throw lastError;
}

/**
 * Fetch jobs with offline support
 * Returns cached data when offline, caches response when online
 */
export async function fetchJobsOffline(params = {}) {
  const isOnline = navigator.onLine;

  // If offline, return cached data immediately
  if (!isOnline) {
    console.log('[OfflineAPI] Offline - returning cached jobs');
    const cachedJobs = await getJobsFromCache(params);
    return {
      jobs: cachedJobs,
      fromCache: true,
      isStale: true
    };
  }

  try {
    // Build query string
    const queryParams = new URLSearchParams();
    if (params.location && params.location !== 'all') {
      queryParams.append('location', params.location);
    }
    if (params.post_type && params.post_type !== 'all') {
      queryParams.append('post_type', params.post_type);
    }
    if (params.category_id && params.category_id !== 'all') {
      queryParams.append('category_id', params.category_id);
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.employer_id && params.employer_id !== 'all') {
      queryParams.append('employer_id', params.employer_id);
    }

    // Fetch with retry
    const response = await fetchWithRetry(
      () => api.get(`/api/jobs?${queryParams.toString()}`)
    );

    const jobs = response.data.jobs || [];

    // Cache the response for offline use (skip when searching/filtering — subset shouldn't overwrite full cache)
    if (jobs.length > 0 && !params.search && !params.employer_id) {
      saveJobsToCache(jobs).catch(console.error);
    }

    return {
      jobs,
      fromCache: false,
      isStale: false
    };
  } catch (error) {
    console.warn('[OfflineAPI] Failed to fetch jobs, using cache:', error.message);
    
    // Fall back to cache on error
    const cachedJobs = await getJobsFromCache(params);
    return {
      jobs: cachedJobs,
      fromCache: true,
      isStale: true,
      error: error.message
    };
  }
}

/**
 * Fetch categories with offline support
 */
export async function fetchCategoriesOffline() {
  const isOnline = navigator.onLine;

  if (!isOnline) {
    console.log('[OfflineAPI] Offline - returning cached categories');
    const cachedCategories = await getCategoriesFromCache();
    return {
      categories: cachedCategories,
      fromCache: true
    };
  }

  try {
    const response = await fetchWithRetry(() => api.get('/api/categories'));
    const categories = response.data.categories || [];

    // Cache for offline use
    if (categories.length > 0) {
      saveCategoriesToCache(categories).catch(console.error);
    }

    return {
      categories,
      fromCache: false
    };
  } catch (error) {
    console.warn('[OfflineAPI] Failed to fetch categories, using cache:', error.message);
    const cachedCategories = await getCategoriesFromCache();
    return {
      categories: cachedCategories,
      fromCache: true,
      error: error.message
    };
  }
}

/**
 * Queue an action for later sync when offline
 */
export async function queueOfflineAction(action) {
  await savePendingAction(action);
  console.log('[OfflineAPI] Action queued for sync:', action.type);
}

/**
 * Sync all pending actions when coming back online
 */
export async function syncPendingActions() {
  if (!navigator.onLine) {
    console.log('[OfflineAPI] Still offline, skipping sync');
    return { synced: 0, failed: 0 };
  }

  const pendingActions = await getPendingActions();
  
  if (pendingActions.length === 0) {
    return { synced: 0, failed: 0 };
  }

  console.log(`[OfflineAPI] Syncing ${pendingActions.length} pending actions`);

  let synced = 0;
  let failed = 0;

  for (const action of pendingActions) {
    try {
      await executeAction(action);
      await removePendingAction(action.id);
      synced++;
    } catch (error) {
      console.error('[OfflineAPI] Failed to sync action:', action, error);
      failed++;
      
      // Update retry count
      if (action.retryCount >= 3) {
        // Remove after too many failures
        await removePendingAction(action.id);
      }
    }
  }

  console.log(`[OfflineAPI] Sync complete: ${synced} synced, ${failed} failed`);
  return { synced, failed };
}

/**
 * Execute a queued action
 */
async function executeAction(action) {
  switch (action.type) {
    case 'SAVE_JOB':
      await api.post(`/api/jobs/${action.payload.jobId}/save`, {}, {
        headers: action.headers
      });
      break;
    
    case 'UNSAVE_JOB':
      await api.delete(`/api/jobs/${action.payload.jobId}/save`, {
        headers: action.headers
      });
      break;
    
    case 'APPLY_JOB':
      await api.post(`/api/jobs/${action.payload.jobId}/apply`, action.payload.data, {
        headers: action.headers
      });
      break;
    
    default:
      console.warn('[OfflineAPI] Unknown action type:', action.type);
  }
}

/**
 * Check if there are pending actions to sync
 */
export async function hasPendingActions() {
  const actions = await getPendingActions();
  return actions.length > 0;
}

export default {
  fetchJobsOffline,
  fetchCategoriesOffline,
  queueOfflineAction,
  syncPendingActions,
  hasPendingActions
};
